// Inhalt einer Tabellenzelle je Spalte der Aktivitäten-Listen (Agent 5C). Die Spaltenschlüssel stehen in
// `aktivitaetenSpalten.ts`; Felder von Custom Activities heißen `feld:<cf_id>`.
import type { MouseEvent, ReactNode } from "react";
import type { AktivitaetZeile, AnrufDetails, CustomDetails, MailDetails, MeetingDetails, NachrichtDetails } from "../api/aktivitaeten";
import { Avatar } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { Telefonnummer, telefonText } from "../components/Telefonnummer";
import { AufnahmeZelle } from "../gespraeche/AufnahmeZelle";
import type { MetaWert } from "../daten/meta";
import { feldText, istLeer } from "../werkzeuge/felder";
import { zeitpunktLang } from "../werkzeuge/zeit";
import { FELD_VORSATZ } from "./aktivitaetenSpalten";
import { ergebnisText, ergebnisTon, gegenueber, statusText, vorname } from "./texte";
import { dauerKurz, datumKurz } from "./zeiten";

export interface ZellenKontext {
  meta: MetaWert;
  /** Lead (und Kontakt) öffnen, z. B. per Router; Rückgabe unbenutzt */
  oeffneLead: (leadId: string, kontaktId?: string | null, aktivitaetId?: string | null) => void;
}

const leer = <span className="leer-wert">—</span>;

function leadLink(z: AktivitaetZeile, ctx: ZellenKontext): ReactNode {
  if (!z.lead_id) {
    // Anrufe ohne Lead (unbekannte Nummer): die Nummer zeigen, sonst steht die Zelle leer
    const d = z.details as Partial<AnrufDetails & NachrichtDetails>;
    const nummer = d.telefon ? telefonText(d.telefon, d.telefon_formatiert) : "";
    return nummer ? <span className="akt-unbekannt" title="Kein Lead zugeordnet">{nummer}</span> : leer;
  }
  const name = z.lead_name ?? "Ohne Namen";
  return (
    <a
      href={`/lead/${encodeURIComponent(z.lead_id)}`}
      className="tabelle__name"
      title={name}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        ctx.oeffneLead(z.lead_id as string, null, z.id);
      }}
    >
      {name}
    </a>
  );
}

function richtungZelle(richtung: string | null | undefined): ReactNode {
  if (richtung !== "ausgehend" && richtung !== "eingehend") return null;
  const aus = richtung === "ausgehend";
  // Auf schmalen Fenstern bleibt nur der Pfeil (siehe aktivitaeten.css)
  return (
    <span className="akt-richtung" title={aus ? "Ausgehend" : "Eingehend"}>
      <Icon name={aus ? "ausgehend" : "eingehend"} groesse={13} className="akt-richtung__symbol" />
      <span className="akt-richtung__text">{aus ? "Ausgehend" : "Eingehend"}</span>
    </span>
  );
}

function nutzerZelle(z: AktivitaetZeile): ReactNode {
  if (!z.user_name) return null;
  return (
    <span className="akt-nutzer" title={z.user_name}>
      <Avatar name={z.user_name} id={z.user_id} groesse={18} />
      <span className="akt-nutzer__name">{vorname(z.user_name)}</span>
    </span>
  );
}

function zeit(iso: string | null): ReactNode {
  const t = datumKurz(iso);
  return t ? <span title={zeitpunktLang(iso)}>{t}</span> : null;
}

export function aktivitaetZelle(z: AktivitaetZeile, key: string, ctx: ZellenKontext): ReactNode {
  if (key.startsWith(FELD_VORSATZ)) return feldZelle(z, key.slice(FELD_VORSATZ.length), ctx);
  const d = z.details as Record<string, unknown>;
  switch (key) {
    case "lead":
      return leadLink(z, ctx);
    case "contact":
      return z.contact_name ? <span title={z.contact_name}>{z.contact_name}</span> : null;
    case "activity_at": {
      const entwurf = z.art === "custom" && (z.details as Partial<CustomDetails>).status === "draft";
      return (
        <>
          {zeit(z.activity_at)}
          {entwurf ? <span className="akt-chip akt-chip--entwurf">Entwurf</span> : null}
        </>
      );
    }
    case "date_updated":
      return zeit(z.date_updated);
    case "richtung":
      return richtungZelle(d.richtung as string | null);
    case "ergebnis": {
      const a = z.details as Partial<AnrufDetails>;
      if (!a.ergebnis) return a.status ? <span className="leise">{statusText(a.status)}</span> : null;
      const ton = ergebnisTon(a.ergebnis);
      return (
        <span className={`akt-ergebnis akt-ergebnis--${ton}`}>
          <Icon name={a.richtung === "eingehend" ? "eingehend" : "ausgehend"} groesse={13} className="akt-pfeil" />
          <span className="akt-ergebnis__text">{ergebnisText(a.ergebnis)}</span>
          {a.in_close === false ? (
            <span className="akt-chip" title={a.fehler || "Dieser Anruf kam nicht bis zum Kunden und steht nicht in Close."}>
              nicht in Close
            </span>
          ) : null}
        </span>
      );
    }
    case "dauer": {
      const a = z.details as Partial<AnrufDetails>;
      return a.dauer ? <span className="akt-dauer">{dauerKurz(a.dauer)}</span> : null;
    }
    case "aufnahme": {
      // Player für Aufnahme bzw. Mailbox-Nachricht (Agent 7D, T47)
      const a = z.details as Partial<AnrufDetails>;
      return <AufnahmeZelle id={z.id} dauerS={a.dauer} ergebnis={a.ergebnis} />;
    }
    case "user":
      return nutzerZelle(z);
    case "text":
      return z.text ? <span title={z.text}>{z.text}</span> : null;
    case "titel":
      return z.titel ? <span title={z.titel}>{z.titel}</span> : null;
    case "telefon": {
      const t = z.details as Partial<AnrufDetails & NachrichtDetails>;
      if (!t.telefon) return null;
      if (!z.lead_id) return <span>{telefonText(t.telefon, t.telefon_formatiert)}</span>;
      return <Telefonnummer nummer={t.telefon} formatiert={t.telefon_formatiert} leadId={z.lead_id} contactId={z.contact_id} kompakt leadName={z.lead_name} kontaktName={z.contact_name} />;
    }
    case "gegenueber": {
      const m = z.details as Partial<MailDetails>;
      const text = gegenueber(m.richtung, m.von, m.an);
      return text ? <span title={[m.von ? `Von: ${m.von}` : "", m.an?.length ? `An: ${m.an.join(", ")}` : ""].filter(Boolean).join("\n")}>{text}</span> : null;
    }
    case "anhaenge": {
      const m = z.details as Partial<MailDetails>;
      return m.anhaenge ? <span className="leise" title={`${m.anhaenge} ${m.anhaenge === 1 ? "Anhang" : "Anhänge"}`}>{m.anhaenge}</span> : null;
    }
    case "status": {
      const t = statusText((d.status as string | null) ?? null);
      return t ? <span>{t}</span> : null;
    }
    case "ort": {
      const ort = (z.details as Partial<MeetingDetails>).ort;
      return ort ? <span title={ort}>{ort}</span> : null;
    }
    case "teilnehmer": {
      const n = (z.details as Partial<MeetingDetails>).teilnehmer;
      return n ? <span>{n}</span> : null;
    }
    default:
      return null;
  }
}

function feldZelle(z: AktivitaetZeile, feldId: string, ctx: ZellenKontext): ReactNode {
  const felder = (z.details as Partial<CustomDetails>).felder ?? {};
  const wert = felder[feldId];
  if (istLeer(wert)) return null;
  const def = ctx.meta.feld(feldId);
  const text = feldText(def, wert as never, { nutzerName: (id) => ctx.meta.nutzerName(id), kontaktName: () => "Kontakt" });
  return text ? <span title={text}>{text}</span> : null;
}
